/**
 * A mutable container for a single {@link Disposable} that can be swapped over
 * time. When a new disposable is set via {@link next}, the previous one is
 * automatically disposed before the replacement is stored.
 *
 * This is useful when a subscription or resource needs to be re-created in
 * response to changing inputs (e.g. a store selector that depends on a key
 * that changes at runtime) while ensuring the old resource is always cleaned up.
 *
 * A `DisposableBox` is itself `Disposable`, so it can be registered with a
 * `DisposableStack` to tie its lifetime to a parent scope. Disposing the box
 * disposes the currently held resource and releases the reference.
 *
 * @example
 * ```ts
 * const box = stack.use(new DisposableBox());
 *
 * // Later, when inputs change:
 * box.next(() => toDisposable(store.subscribe(callback)));
 *
 * // The previous subscription is disposed automatically.
 * ```
 */
export class DisposableBox {
    #disposable;
    constructor(disposable) {
        if (disposable !== undefined) {
            this.#disposable = typeof disposable === 'function' ? disposable() : disposable;
        }
    }
    get current() {
        return this.#disposable;
    }
    next(disposable) {
        this.#disposable?.[Symbol.dispose]();
        this.#disposable = undefined;
        this.#disposable = typeof disposable === 'function' ? disposable() : disposable;
    }
    /**
     * Detaches and returns the currently held disposable without disposing it.
     * The caller assumes ownership of the returned resource.
     *
     * @example
     * ```ts
     * function createResource(): MyResource {
     *     using box = new DisposableBox(() => new MyResource());
     *     // ... setup that might throw ...
     *     return box.release()!; // caller takes ownership
     * }
     * // If setup throws, the box is disposed, cleaning up the resource.
     * // On success, release() detaches it so disposal is a no-op.
     * ```
     */
    release() {
        const disposable = this.#disposable;
        this.#disposable = undefined;
        return disposable;
    }
    clear() {
        this.#disposable?.[Symbol.dispose]();
        this.#disposable = undefined;
    }
    [Symbol.dispose]() {
        this.clear();
    }
}
